import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, Archive, Link as LinkIcon, FileText, Camera, HeartHandshake } from "lucide-react";
import client from "../lib/api";

/**
 * RoadmapCard.
 *
 * Renders the deterministic 3-5 milestone roadmap for a pledge.
 * Each milestone can be closed with a lightweight, self-attested
 * piece of evidence (link, dated note, or photo caption).
 *
 * The care-rail wins over closing: if the person types a distress
 * phrase into the evidence value, the API returns status=care_rail
 * and this card leaves the milestone open, then surfaces the
 * support message inline.
 *
 * The first time the card mounts and the pledge has no milestones
 * yet, it silently calls /roadmap/generate so a live pledge always
 * has a live roadmap.
 */
export function RoadmapCard({ pledge }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [evidenceKind, setEvidenceKind] = useState("dated_note");
  const [evidenceValue, setEvidenceValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [reprompt, setReprompt] = useState(null);
  const [careRail, setCareRail] = useState(null);

  const load = useCallback(async () => {
    if (!pledge?.id) return;
    setLoading(true);
    try {
      const { data } = await client.get(`/accountability/pledge/${pledge.id}/roadmap`);
      let list = data?.milestones || [];
      if (!list.length && pledge.source_session_id) {
        const gen = await client.post(`/accountability/pledge/${pledge.id}/roadmap/generate`);
        list = gen.data?.milestones || [];
      }
      setMilestones(list);
    } catch (_e) { setMilestones([]); }
    finally { setLoading(false); }
  }, [pledge?.id, pledge?.source_session_id]);

  useEffect(() => { load(); }, [load]);

  const closeMilestone = async (order) => {
    if (!evidenceValue.trim()) return;
    setBusy(true); setReprompt(null); setCareRail(null);
    try {
      const { data } = await client.post(
        `/accountability/pledge/${pledge.id}/milestone/${order}/close`,
        { evidence_kind: evidenceKind, evidence_value: evidenceValue.trim() },
      );
      if (data.status === "care_rail") {
        setCareRail(data.message);
        return;
      }
      if (data.status === "closed") {
        setOpenId(null); setEvidenceValue("");
        await load();
      }
    } catch (_e) {
      setReprompt("Klair could not close this just now. Please try again.");
    } finally { setBusy(false); }
  };

  const archiveMilestone = async (order) => {
    if (!window.confirm("Archive this milestone? It will not be deleted, just set aside.")) return;
    setBusy(true);
    try {
      await client.post(
        `/accountability/pledge/${pledge.id}/milestone/${order}/archive`,
        { archive_reason: "superseded" },
      );
      await load();
    } catch (_e) { /* silent */ } finally { setBusy(false); }
  };

  if (loading || !pledge) return null;
  if (!milestones.length) return null;

  return (
    <div className="k-card mt-6" data-testid="roadmap-card">
      <div className="k-label mb-3">the shape of the next few weeks</div>
      <ol className="space-y-3">
        {milestones.map((m) => (
          <li key={m.order} className="klareet-serif" data-testid={`milestone-${m.order}`}>
            <div className="flex items-start gap-3">
              <div className="mt-1">
                {m.state === "closed" ? (
                  <CheckCircle2 size={18} data-testid={`milestone-${m.order}-closed-icon`} />
                ) : m.state === "archived" ? (
                  <Archive size={18} data-testid={`milestone-${m.order}-archived-icon`} />
                ) : (
                  <Circle size={18} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div style={{ fontSize: "1rem", lineHeight: 1.35 }} data-testid={`milestone-${m.order}-title`}>
                  {m.title}
                </div>
                <p className="text-sm mt-1" style={{ lineHeight: 1.5, opacity: 0.85 }} data-testid={`milestone-${m.order}-description`}>
                  {m.description}
                </p>
                {m.grounding && (
                  <div className="k-quiet !text-xs mt-2 klareet-serif" style={{ opacity: 0.75 }} data-testid={`milestone-${m.order}-grounding`}>
                    you said: &ldquo;{m.grounding}&rdquo;. the first move sits with that.
                  </div>
                )}
                {m.state === "closed" && m.evidence && (
                  <div className="k-quiet !text-xs mt-2" data-testid={`milestone-${m.order}-evidence`}>
                    kept, {m.evidence.kind.replace(/_/g, " ")}
                  </div>
                )}
                {m.state === "open" && openId !== m.order && (
                  <div className="flex gap-3 mt-2">
                    <button
                      onClick={() => { setOpenId(m.order); setReprompt(null); setCareRail(null); }}
                      className="k-btn k-btn-ghost !text-xs"
                      data-testid={`milestone-${m.order}-open-close`}
                    >
                      keep this one
                    </button>
                    <button
                      onClick={() => archiveMilestone(m.order)}
                      disabled={busy}
                      className="k-btn k-btn-ghost !text-xs"
                      data-testid={`milestone-${m.order}-archive`}
                    >
                      set aside
                    </button>
                  </div>
                )}
                {m.state === "open" && openId === m.order && (
                  <div className="mt-3 p-3 k-warm-card" data-testid={`milestone-${m.order}-form`}>
                    <div className="k-label mb-2">how did you keep it</div>
                    <div className="flex gap-2 mb-2 flex-wrap">
                      <EvidencePill kind="link" current={evidenceKind} setKind={setEvidenceKind} icon={<LinkIcon size={12} />} />
                      <EvidencePill kind="dated_note" current={evidenceKind} setKind={setEvidenceKind} icon={<FileText size={12} />} />
                      <EvidencePill kind="photo_caption" current={evidenceKind} setKind={setEvidenceKind} icon={<Camera size={12} />} />
                    </div>
                    <textarea
                      className="k-input klareet-serif"
                      rows={2}
                      maxLength={800}
                      value={evidenceValue}
                      onChange={(e) => setEvidenceValue(e.target.value)}
                      placeholder={
                        evidenceKind === "link" ? "paste a link" :
                        evidenceKind === "photo_caption" ? "describe the photo, one line" :
                        "a dated note, one sentence is enough"
                      }
                      data-testid={`milestone-${m.order}-input`}
                    />
                    {reprompt && (
                      <div className="k-quiet mt-2 !text-xs" data-testid={`milestone-${m.order}-reprompt`}>
                        {reprompt}
                      </div>
                    )}
                    {careRail && (
                      <div className="k-warm-card mt-2 p-2 text-sm" data-testid={`milestone-${m.order}-care-rail`}>
                        <HeartHandshake size={12} className="inline mr-2" />
                        {careRail}
                      </div>
                    )}
                    <div className="flex justify-end mt-2 gap-2">
                      <button
                        onClick={() => { setOpenId(null); setEvidenceValue(""); }}
                        className="k-btn k-btn-ghost !text-xs"
                        data-testid={`milestone-${m.order}-cancel`}
                      >
                        not yet
                      </button>
                      <button
                        onClick={() => closeMilestone(m.order)}
                        disabled={busy || !evidenceValue.trim()}
                        className="k-btn k-btn-primary !text-xs"
                        data-testid={`milestone-${m.order}-submit`}
                      >
                        {busy ? "saving" : "kept"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function EvidencePill({ kind, current, setKind, icon }) {
  const active = current === kind;
  return (
    <button
      onClick={() => setKind(kind)}
      className={`k-btn !text-xs !py-1 !px-2 ${active ? "k-btn-primary" : "k-btn-ghost"}`}
      data-testid={`evidence-pill-${kind}`}
    >
      {icon}<span className="ml-2">{kind.replace(/_/g, " ")}</span>
    </button>
  );
}
